import React, { useState, useEffect } from 'react';
import { Text, Alert, AlertIcon, AlertDescription, Flex, Button } from '@chakra-ui/react';
import moment from 'moment';

import {
  DateInputWithConfirm,
  InputWithConfirm,
  UploadActions,
  UploadExample,
  UploadButtonSection,
  UploadComplete as UploadCompleteSection,
  ModalColumns,
  AcknowledgeBox,
  getStringFromDate,
  getDateFromString,
  RequirementsRemedy
} from 'shared-components';

import { isValidDate } from '../../../../utilities/validation';
import { IDate } from '../../../../types';

export interface IProps {
  docNumber?: string;
  placeOfIssue?: string;
  dateOfIssue?: string;
  dateOfExpiry?: string;
  passportInvalidForCourse?: boolean;
  complete: boolean;
  fileNames: string[];
  onContinue: () => void;
  onUpload: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    docTitle: string,
    fileNames: string[],
    metadata: any
  ) => void;
  onClose: () => void;
  docType: 'current' | 'renew';
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
  passportUrl: string;
}

export const validateDate = (dateValue: IDate): string => {
  const { YYYY, DD, MM } = dateValue;
  if (YYYY && DD && MM) {
    const isValid = isValidDate(dateValue);
    if (!isValid) {
      return "Doesn't look like a valid date";
    }
  }
  return '';
};

export const PassportModal: React.FC<IProps> = (props: IProps) => {
  const { docType, docKey, fileNames, complete, onUpload, onContinue, infoOnly, docStatus, passportUrl } = props;

  const [docNumber, setDocNumber] = useState(props.docNumber ?? '');
  const [placeOfIssue, setPlaceOfIssue] = useState(props.placeOfIssue ?? '');
  const [dateOfIssue, setDateOfIssue] = useState(getDateFromString(props.dateOfIssue));
  const [dateOfIssueError, setDateOfIssueError] = useState<string | undefined>();
  const [dateOfExpiry, setDateOfExpiry] = useState(getDateFromString(props.dateOfExpiry));
  const [dateOfExpiryError, setDateOfExpiryError] = useState('');

  const [questionsComplete, setQuestionsComplete] = useState(
    !!props.docNumber &&
      props.docNumber !== '' &&
      !!props.placeOfIssue &&
      props.placeOfIssue !== '' &&
      !!props.dateOfIssue &&
      props.dateOfIssue !== '' &&
      !!props.dateOfExpiry &&
      props.dateOfExpiry !== ''
  );

  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);

  useEffect(() => {
    const isDateOfIssueEmpty = getStringFromDate(dateOfIssue) === '';
    const isDateOfExpiryEmpty = getStringFromDate(dateOfExpiry) === '';

    setIsSaveButtonEnabled(
      docNumber !== '' &&
        placeOfIssue !== '' &&
        !isDateOfIssueEmpty &&
        !isDateOfExpiryEmpty &&
        dateOfIssueError === '' &&
        dateOfExpiryError === ''
    );
  }, [docNumber, placeOfIssue, dateOfIssue, dateOfExpiry, dateOfIssueError, dateOfExpiryError]);

  const getFileInstruction = (complete: boolean) => {
    if (complete) {
      if (docType === 'current' && props.passportInvalidForCourse) {
        return (
          <>
            <Alert status="warning" mb={6}>
              <AlertIcon />

              <AlertDescription>
                Your current passport expires on <b>{moment(props.dateOfExpiry).format('DD MMM YYYY')}</b> which is less
                than 6 months after your course start date.
              </AlertDescription>
            </Alert>

            <RequirementsRemedy>
              You will need to apply for a new passport and upload it to the section below this one labelled
              "new/renewed passport" on your document upload page.
            </RequirementsRemedy>
          </>
        );
      }
      return <></>;
    } else {
      return (
        <Text>
          Awaiting upload of <b>Passport Scan</b>.
        </Text>
      );
    }
  };

  const handleUpload = () => {
    setQuestionsComplete(true);
    onUpload('passport', null, docType, docKey, 'Passport Scan', fileNames, {
      docNumber,
      placeOfIssue,
      dateOfIssue: getStringFromDate(dateOfIssue),
      dateOfExpiry: getStringFromDate(dateOfExpiry)
    });
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  return (
    <ModalColumns
      id={'passport'}
      title={'Passport Scan'}
      onClose={handleOnClose}
      supportingColumn={<UploadExample altText="Passport example" url={passportUrl} />}
    >
      {infoOnly ? (
        <>
          <Text>Be aware that a scan of your passport may be required during your visa application.</Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
            label="Return to document upload"
          />
        </>
      ) : (
        <>
          {!questionsComplete && (
            <>
              <InputWithConfirm
                key={'docNumber'}
                id={'docNumber'}
                value={docNumber}
                label={'Passport or Travel Document Number'}
                onChange={(evt: any) => setDocNumber(evt.currentTarget.value)}
              />

              <InputWithConfirm
                key={'placeOfIssue'}
                id={'placeOfIssue'}
                value={placeOfIssue}
                label={'Place of Issue'}
                onChange={(evt: any) => {
                  setPlaceOfIssue(evt.currentTarget.value);
                }}
              />

              <DateInputWithConfirm
                key={'dateOfIssue'}
                id={'dateOfIssue'}
                value={dateOfIssue}
                label={'Date of Issue'}
                errorText={dateOfIssueError}
                onChangeDD={(evt: any) => {
                  const d = {
                    ...dateOfIssue,
                    DD: evt.currentTarget.value
                  };
                  setDateOfIssue(d);
                }}
                onChangeMM={(evt: any) => {
                  const d = {
                    ...dateOfIssue,
                    MM: evt.currentTarget.value
                  };
                  setDateOfIssue(d);
                }}
                onChangeYY={(evt: any) => {
                  const value: string = evt.currentTarget.value;
                  if (value.length === 4) {
                    const d = {
                      ...dateOfIssue,
                      YYYY: value
                    };
                    setDateOfIssue(d);

                    if (
                      validateDate({
                        ...dateOfIssue,
                        YYYY: value
                      })
                    ) {
                      setDateOfIssueError("Doesn't appear to be a valid date");
                    } else {
                      setDateOfIssueError('');
                    }
                  } else {
                    const d = {
                      ...dateOfIssue,
                      YYYY: value
                    };
                    setDateOfIssue(d);
                  }
                }}
                onBlur={(_evt: unknown) => {
                  if (dateOfIssue.DD && dateOfIssue.MM && dateOfIssue.YYYY) {
                    if (validateDate({ ...dateOfIssue })) {
                      setDateOfIssueError("Doesn't appear to be a valid date");
                    } else {
                      setDateOfIssueError('');
                    }
                  }
                }}
              />

              <DateInputWithConfirm
                key={'dateOfExpiry'}
                id={'dateOfExpiry'}
                value={dateOfExpiry}
                label={'Date of Expiry'}
                errorText={dateOfExpiryError}
                onChangeDD={(evt: any) => {
                  const d = {
                    ...dateOfExpiry,
                    DD: evt.currentTarget.value
                  };
                  setDateOfExpiry(d);
                }}
                onChangeMM={(evt: any) => {
                  const d = {
                    ...dateOfExpiry,
                    MM: evt.currentTarget.value
                  };
                  setDateOfExpiry(d);
                }}
                onChangeYY={(evt: any) => {
                  const value: string = evt.currentTarget.value;
                  if (value.length === 4) {
                    const d = {
                      ...dateOfExpiry,
                      YYYY: value
                    };
                    setDateOfExpiry(d);

                    if (validateDate({ ...dateOfExpiry, YYYY: value })) {
                      setDateOfExpiryError("Doesn't appear to be a valid date");
                    } else {
                      setDateOfExpiryError('');
                    }
                  } else {
                    const d = {
                      ...dateOfExpiry,
                      YYYY: value
                    };
                    setDateOfExpiry(d);
                  }
                }}
                onBlur={(evt: any) => {
                  if (dateOfExpiry.DD && dateOfExpiry.MM && dateOfExpiry.YYYY) {
                    if (
                      validateDate({
                        ...dateOfExpiry
                      })
                    ) {
                      setDateOfExpiryError("Doesn't appear to be a valid date");
                    } else {
                      setDateOfExpiryError('');
                    }
                  }
                }}
              />

              <Flex justifyContent="end">
                <Button
                  id="password-questions"
                  isDisabled={!isSaveButtonEnabled}
                  onClick={handleUpload}
                  variant="solid"
                  size="lg"
                >
                  Save &amp; Continue
                </Button>
              </Flex>
            </>
          )}

          {questionsComplete &&
            (complete ? (
              <>
                {getFileInstruction(complete)}
                <UploadCompleteSection fileNames={fileNames} onUpload={handleUpload} onContinue={onContinue} />
              </>
            ) : (
              <>
                <UploadButtonSection onUpload={handleUpload}>{getFileInstruction(complete)}</UploadButtonSection>

                <UploadActions complete={complete} onContinue={onContinue} />
              </>
            ))}
        </>
      )}
    </ModalColumns>
  );
};

export default PassportModal;
